"use strict";

/**
 * Layer-Auswahl-Modus für die Feature-Infofunktion.
 */
gc.gis.module.GCFeatureInfoConstants = {
    /**
     * Sichtbare Layer abfragen.
     */
    QUERY_LAYER_VISIBLE: 1,
    /**
     * Ausgewählte Layer abfragen.
     */
    QUERY_LAYER_SELECTED: 2,
    /**
     * Alle Layer abfragen.
     */
    QUERY_LAYER_ALL: 4,
    /**
     * Einzelnen Layer abfragen.
     */
    QUERY_LAYER_SINGLE: 8,

    /**
     * Identify per Punkt.
     */
    IDENTIFY_POINT: 1,
    /**
     * Identify per Rechteck.
     */
    IDENTIFY_RECTANGLE: 2,
    /**
     * Identify per Box.
     */
    IDENTIFY_BOX: 4,
    /**
     * Identify per Polygon.
     */
    IDENTIFY_POLYGON: 8
};

/**
 * Events der Nutzeraktion des FeatureInfo-Moduls.
 * @enum {string}
 */
gc.gis.module.GCFeatureInfoEvent = {
    /**
     * Wird ausgelöst, wenn der Nutzer den Geometrie-Modus für die Abfrage geändert hat.
     */
    GEOMETRY_TYPE_CHANGED:      'geommode:changed',
    /**
     * Wird ausgelöst, wenn der Nutzer die Abfrage nach den Geometrien der Zeichenfläche ausgelöst hat.
     */
    QUERY_DRAWINGAREA:          'query:drawingarea',
    /**
     * Wird ausgelöst, wenn der Nutzer den Layer-Modus für die Abfrage geändert hat.
     */
    LAYER_MODE_CHANGED:         'layermode:changed',
    /**
     * Wird ausgelöst, wenn der Info-Dialog geschlossen wird.
     */
    INFO_DIALOG_CLOSED:         'dialog:closed',
    /**
     * Wird ausgelöst, wenn der Nutzer den Butten zum zurücksetzen der Ergebisliste gedrückt hat.
     */
    RESULT_RESET:               'result:reset',

    RESULT_LAYER_MOUSEENTER:    'result:layer_mouseenter',

    RESULT_LAYER_MOUSELEAVE:    'result:layer_mouseleave',

    RESULT_LAYER_OPEN:          'result:layer_open',

    RESULT_LAYER_CLOSE:         'result:layer_close',

    RESULT_LAYER_ZOOM:          'result:layer_zoom',

    RESULT_FEATURE_MOUSEENTER:  'result:feature_mouseenter',

    RESULT_FEATURE_MOUSELEAVE:  'result:feature_mouseleave',

    RESULT_FEATURE_ADD:         'result:feature_add',

    RESULT_FEATURE_OPEN:        'result:feature_open',

    RESULT_FEATURE_CLOSE:       'result:feature_close',

    RESULT_FEATURE_CLICK:       'result:feature_click',

    RESULT_FEATURE_ZOOM:        'result:feature_zoom'
};

/**
 * ContentTypen des FeatureInfo-Moduls.
 * @enum {string}
 */
gc.gis.module.GCFeatureInfoDetailResultTypes = {
    INLINE_HTML:        'inline-html',
    HTML:               'html',
    HTML_THIRD_PARTY:   'html-third-party'
};

/**
 * @classdesc
 * Feature-Info-Modul.
 *
 * @abstract
 * @class
 * @extends gc.gis.module.GCAbstractModule
 * @memberof gc.gis.module
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {string} [options.label='Objekt-Information'] Label
 * @param {int} options.maxFeatures max. Anzahl abfragbarer Features
 */
gc.gis.module.GCFeatureInfo = function(options)
{
	gc.gis.module.GCAbstractModule.call(this, options);

    /**
     * @type {string}
     */
	this.label = options.label || 'Objekt-Information';

    /**
     * Maximale Anzahl der Features pro Layer.
     * @type {number}
     */
    this.max_features = options.maxFeatures || 5000;

    /**
     * Gibt an, ob das Ergebnis innerhalb der Sidebar dargestellt werden soll. Alternativ erfolgt eine Ausgabe der
     * Abfrage-Ergebnisse im Popup.
     * @type {boolean}
     * @private
     */
    this._sidebar_mode = false;
    /**
     * Referenz auf die Kartenansicht
     * @type {gc.gis.map.GCMapView}
     * @private
     */
    this._map = gc.gis.iface.map;
    /**
     * Dialog, der zur Anzeige diverser Ergebnisse benutzt wird
     * @type {gc.gis.module.GCFeatureInfo.InfoDialog}
     * @private
     */
    this._info_dialog = new gc.gis.module.GCFeatureInfo.InfoDialog();
    /**
     * Container-Element zur Verwaltung der Abfrageergebnisse innerhalb der Sidebar.
     * @type {gc.gis.module.GCFeatureInfo.LayerResultList}
     * @private
     */
    this._result_list_sidebar = new gc.gis.module.GCFeatureInfo.LayerResultList(
        gc.gis.module.GCFeatureInfo.FeatureResultListSidebar, {
            maxFeatures: this.max_features
        });
    /**
     * Container-Element zur Verwaltung der Abfrageergebnisse innerhalb des Popup.
     * @type {gc.gis.module.GCFeatureInfo.LayerResultList}
     * @private
     */
    this._result_list_popup = new gc.gis.module.GCFeatureInfo.LayerResultList(
        gc.gis.module.GCFeatureInfo.FeatureResultListPopup, {
            maxFeatures: this.max_features
        });

    /**
     * Tracking-Layer des Erfassungswerkzeugs für entsprechende Abfragen
     * @type {gc.gis.layer.GCTrackingLayer}
     * @private
     */
    this._digitizeTrackingLayer = undefined;

    // Zeichnen-Interaktion definieren
	var drawStroke = gc.gis.iface.mapFactory.createStyleStroke();
	drawStroke.setWidth(2).setColor(new gc.gis.style.GCColor([0, 0, 255, 0.9]));
    /**
     * Zeichnen-Interaktion
     * @type {gc.gis.map.GCMapDrawInteraction}
     * @private
     */
	this._mapInteraction = gc.gis.iface.mapFactory.createMapDrawInteraction(this._map, {
	    drawingStyle: [drawStroke],
        finalStyle: [drawStroke]
    });

    /**
     * Tracking-Layer für Highlighting der Abfrage-Ergebnisse
     * @type {gc.gis.layer.GCTrackingLayer}
     * @private
     */
    this._trackingLayer = gc.gis.iface.mapFactory.createTrackingLayer(this._map.getMap());
    /**
     * Tracking-Layer für die Hervorhebung aktiver Abfrage-Ergebnisse
     * @type {gc.gis.layer.GCTrackingLayer}
     * @private
     */
    this._trackingLayerActive = gc.gis.iface.mapFactory.createTrackingLayer(this._map.getMap());

    // Tracking-Layer initialisieren (Styles etc.)
    this.__initTrackingLayer();

	// Geräteabhängige Hilfe
	if( gc.gis.iface.env.device === gc.gis.env.device.MOBILE )
	{
        this.interaction_message = {
            title:   this.label,
            icon:    'touch-pointer',
            message: 'Markieren Sie eine oder mehrere Ebenen und tippen Sie auf ein Objekt auf der Karte.'
        };
	}
	else
	{
        this.interaction_message = {
            title:   this.label,
            icon:    'mouse-button-left',
            message: 'Markieren Sie eine oder mehrere Ebenen und klicken Sie anschließend mit der linken Maustaste auf ein Objekt auf der Karte.'
        };
	}

    this._render();
    this.__initEvents();

    /**
     * Aktuell angestellter Geometrietyp zum Abfragen der Objekt-Informationen.
     * Dieser Member sollte niemals direkt schreibend beutzt werden. Sondern stets über die Methode _setQueryGeometryType.
     * @type {int|undefined}
     * @private
     */
    this._queryGeometryType = undefined;
    this._setQueryGeometryType(gc.gis.module.GCFeatureInfoConstants.IDENTIFY_POINT);

	// Sidebar-Panel
    this.launcher_content = new gc.gis.ui.GCSidebarContent({
        label:   this.label,
        'parent': null,
        element: this.getElement()
    });
	this.launcher = {
		label: this.label,
		icon: 'ms ms-identify',
		content: this.launcher_content
	};
	gc.gis.iface.ui.sidebar.addLauncher( this.launcher );
};
gc.gis.module.GCFeatureInfo.prototype = Object.create( gc.gis.module.GCAbstractModule.prototype );

/**
 * Initialisiert Layer-Events am Kartenobjekt.
 *
 * @private
 * @method
 */
gc.gis.module.GCFeatureInfo.prototype.__initEvents = function()
{
    // Abhängiges Modul überwachen
    gc.gis.iface.on(gc.gis.GCApplicationEvent.MODULE_LOADED, this, function (evt) {
        if (evt.instance instanceof gc.gis.module.GCDigitize) {
            this._digitizeTrackingLayer = evt.instance.getLayer();
        }
    });

    // Interaktion aktivieren, wenn Panel aktiv
    gc.gis.iface.ui.sidebar.on(gc.gis.ui.GCSidebarEvent.CONTENT_CHANGED, this, function(evt) {
        if(evt.newValue === this.launcher_content)
        {
            this._sidebar_mode = true;
            this._setQueryGeometryType(this.element_panel.getGeometryType());
            gc.gis.iface.ui.setInteractionMessage(this.interaction_message);
        }
        else if(evt.oldValue === this.launcher_content)
        {
            this._sidebar_mode = false;
            this._setQueryGeometryType(gc.gis.module.GCFeatureInfoConstants.IDENTIFY_POINT);
            gc.gis.iface.ui.unsetInteractionMessage(this.interaction_message);
        }
    });

    // Interaktion, wenn Kartenaktionen anderer Module aktiv werden (z.B. Messen-Modul)
    this._map.on(gc.gis.map.GCMapEvent.INTERACTION_ACTIVATED, this, function(evt) {
        // fremde Module aktivieren eine Karteninteraktion => alle Kartenaktionen und Klick-Handler deaktivieren
        if (evt.interaction !== this._mapInteraction.getImpl())
        {
            this._setQueryGeometryType(undefined);
        }
    });
    this._map.on(gc.gis.map.GCMapEvent.INTERACTION_DEACTIVATED, this, function(evt) {
        // fremde Module deaktivieren Interaktion => einfache Abfrage aktivieren
        if (evt.interaction !== this._mapInteraction.getImpl())
        {
            this._setQueryGeometryType(this._sidebar_mode ?
	            this.element_panel.getGeometryType() :
                gc.gis.module.GCFeatureInfoConstants.IDENTIFY_POINT);
        }
    });

	// Reaktion auf Kartenaktionen
    this._mapInteraction.on(gc.gis.map.GCMapDrawInteractionEvent.DRAWEND, this, function(evt) {
        this._doFeatureInfo(evt.feature.getGeometry());
    });
    this._mapInteraction.on(gc.gis.map.GCMapDrawInteractionEvent.DRAWSTART, this, function(evt) {
        this._mapInteraction.clear();
    });

    // Einstellungen der Nutzer verarbeiten
    this.element_panel.on(gc.gis.module.GCFeatureInfoEvent.GEOMETRY_TYPE_CHANGED, this, function(evt) {
        this._doGeometryTypeChanged(evt.old_type, evt.new_type);
    });
    // Einstellungen der Nutzer verarbeiten
    this.element_panel.on(gc.gis.module.GCFeatureInfoEvent.QUERY_DRAWINGAREA, this, function(evt) {
        this._doQueryDrawingArea();
    });
    this.element_panel.on(gc.gis.module.GCFeatureInfoEvent.LAYER_MODE_CHANGED, this, function(evt) {
        this._doLayerSelectionModeChanged(evt.old_mode, evt.new_mode);
    });
    this.element_panel.on(gc.gis.module.GCFeatureInfoEvent.RESULT_RESET, this, function(evt) {
        this._resetResult();
    });

    // Aktivitäten der Nutzer auf der Ergebnisliste verarbeiten
    this._info_dialog.on(gc.gis.module.GCFeatureInfoEvent.INFO_DIALOG_CLOSED, this, function() {
        this._onInfoDialogClosed();
    });

    // Aktivitäten auf der Sidebar Ergebnis-Liste
    this._result_list_sidebar.on(gc.gis.module.GCFeatureInfoEvent.RESULT_LAYER_OPEN, this, function(evt) {
        this._highlightFeatures(evt.features);
    });
    this._result_list_sidebar.on(gc.gis.module.GCFeatureInfoEvent.RESULT_LAYER_CLOSE, this, function(evt) {
        this._unHighlightFeatures(evt.features);
    });
    this._result_list_sidebar.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_CLICK, this, function(evt) {
        this._onSidebarFeatureClick(evt);
    });
    this._result_list_sidebar.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_MOUSEENTER, this, function(evt) {
        this._highlightActiveFeatures(evt.feature);
    });
    this._result_list_sidebar.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_MOUSELEAVE, this, function(evt) {
        this._unHighlightActiveFeatures(evt.feature);
    });
    this._result_list_sidebar.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_ZOOM, this, function(evt) {
        this._doZoomFeature(evt.feature);
    });

    // Aktivitäten auf der Popup Ergebnis-Liste
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_LAYER_OPEN, this, function(evt) {
        this._highlightFeatures(evt.features);
    });
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_LAYER_CLOSE, this, function(evt) {
        this._unHighlightFeatures(evt.features);
    });
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_OPEN, this, function(evt) {
        this._onPopupFeatureOpen(evt);
    });
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_CLOSE, this, function(evt) {
        // ??
    });
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_MOUSEENTER, this, function(evt) {
        this._highlightActiveFeatures(evt.feature);
    });
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_MOUSELEAVE, this, function(evt) {
        this._unHighlightActiveFeatures(evt.feature);
    });
    this._result_list_popup.on(gc.gis.module.GCFeatureInfoEvent.RESULT_FEATURE_ZOOM, this, function(evt) {
        this._doZoomFeature(evt.feature);
    });
};

/**
 * @method
 * @return {jQuery}
 * @public
 */
gc.gis.module.GCFeatureInfo.prototype.getElement = function()
{
	return this.element_panel.getElement();
};

/**
 * Liefert die Referenz auf die zugrunde liegende Kartenansicht.
 *
 * @method
 * @return {gc.gis.map.GCMapView}
 * @public
 */
gc.gis.module.GCFeatureInfo.prototype.getMap = function()
{
    return this._map;
};

/**
 * Liefert das Karteninteraktions-Objekt.
 *
 * @method
 * @return {gc.gis.map.GCMapDrawInteraction}
 * @public
 */
gc.gis.module.GCFeatureInfo.prototype.getMapIntercation = function()
{
    return this._mapInteraction;
};

/**
 * Erzeugt den Content des Moduls.
 *
 * @method
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._render = function()
{
	this.element_panel = new gc.gis.module.GCFeatureInfo.Panel({
		module: this,
        resultList: this._result_list_sidebar,
		active: this.active,
		headline: this.label
	});
};

/**
 * Initialisiert die Tracking-Layer.
 *
 * @method
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype.__initTrackingLayer = function()
{
	this._trackingLayer.setStyle(gc.gis.iface.styleFactory.getStyle(gc.gis.factory.StyleType.HIGHLIGHT));
    this._map.getMap().addTrackingLayer(this._trackingLayer);

	this._trackingLayerActive.setStyle(gc.gis.iface.styleFactory.getStyle(gc.gis.factory.StyleType.HIGHLIGHT_ACTIVE));
	this._map.getMap().addTrackingLayer(this._trackingLayerActive);
};

/**
 * Liefert die abzufragenden Layer gemäß Nutzerauswahl bzw. Modus.
 *
 * @method
 * @return {gc.gis.layer.GCAbstractLayer[]}
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._getQueryLayers = function()
{
    var layers = [];
    if (this._sidebar_mode)
    {
        switch (this.element_panel.getLayerSelectionMode())
        {
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_VISIBLE:
                layers = this._map.getLayersRecursive(function (layer) {
                    return !(layer instanceof gc.gis.layer.GCGroupLayer)
                        && layer.isReallyVisible()
                        && layer.isQueryable();
                });
                break;
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_SELECTED:
                layers = this._map.getLayersRecursive(function (layer) {
                    return !(layer instanceof gc.gis.layer.GCGroupLayer)
                        && layer.isSelected()
                        && layer.isQueryable();
                });
                break;
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_ALL:
                layers = this._map.getLayersRecursive(function (layer) {
                    return !(layer instanceof gc.gis.layer.GCGroupLayer)
                        && layer.isQueryable();
                });
                break;
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_SINGLE:
                if (this.element_panel.getSelectedLayer())
                {
                    layers = [this.element_panel.getSelectedLayer()];
                }
                break;
        }
    }
    else
    {
        layers = this._map.getLayersRecursive(function (layer) {
            return !(layer instanceof gc.gis.layer.GCGroupLayer)
                && layer.isReallyVisible()
                && layer.isQueryable();
        });
    }

    return layers;
};

/**
 * Setzt den Geometrietype für die Abfrage.
 *
 * @method
 * @param {int} type
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._setQueryGeometryType = function(type)
{
    this._queryGeometryType = type;

    this._mapInteraction.clear();
    this._map.un(gc.gis.map.GCMapEvent.SINGLECLICK, this);  // !! immer Event entfernen

    switch (type)
    {
        case gc.gis.module.GCFeatureInfoConstants.IDENTIFY_POLYGON:
            this._mapInteraction.setGeometryType(gc.gis.geom.GCGeometryType.POLYGON);
            break;
        case gc.gis.module.GCFeatureInfoConstants.IDENTIFY_BOX:
            this._mapInteraction.setGeometryType(gc.gis.geom.GCGeometryType.BOX);
            break;
        case gc.gis.module.GCFeatureInfoConstants.IDENTIFY_RECTANGLE:
            this._mapInteraction.setGeometryType(gc.gis.geom.GCGeometryType.RECTANGLE);
            break;
    }

    if (type === gc.gis.module.GCFeatureInfoConstants.IDENTIFY_POINT)
    {
        this._mapInteraction.setActive(false);
        this._map.on(gc.gis.map.GCMapEvent.SINGLECLICK, this, function(evt) {
            var point = gc.gis.iface.mapFactory.createPoint();
            point.setXY(evt.coordinate[0], evt.coordinate[1]);
            point.setCRS(this._map.getProjection());
            this._doFeatureInfo(point);
        });
    }
    else if (type !== undefined)
    {
        this._mapInteraction.setActive(true);
    }
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn der Layer-Modus geändert wurde.
 *
 * @method
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._doLayerSelectionModeChanged = function(old_mode, new_mode)
{
    var layers = this._getQueryLayers();
    if (this._sidebar_mode && layers.length === 0)
    {
        switch (new_mode) {
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_VISIBLE:
                gc.gis.iface.log.info(this, 'Zurzeit werden keine abfragbarenen Ebenen angezeigt.');
                break;
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_SELECTED:
                gc.gis.iface.log.info(this, 'Es wurden keine Ebenen ausgewählt. ' +
                    'Nutzen Sie zur Auswahl das Menü "Ebenen".');
                break;
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_ALL:
                gc.gis.iface.log.info(this, 'Zurzeit stehen keine abfragbarenen Ebenen zur Verfügung.');
                break;
            case gc.gis.module.GCFeatureInfoConstants.QUERY_LAYER_SINGLE:
                gc.gis.iface.log.info(this, 'Es wurde keine Ebene ausgewählt.');
                break;
        }
    }
};

/**
 * @method
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._doGeometryTypeChanged = function(old_type, new_type)
{
    this._setQueryGeometryType(new_type);
};

/**
 * @method
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._doQueryDrawingArea = function()
{
    var geoms = [],
        features,
        i, ii;

    if (this._digitizeTrackingLayer && this._digitizeTrackingLayer.numFeatures() > 0)
    {
        // TODO evtl. im Trackinglayer eine getGeometries implementieren
        features = this._digitizeTrackingLayer.getFeatures();
        for (i = 0, ii = features.length; i < ii; i++)
        {
            geoms.push(features[i].getGeometry());
        }
        this._doFeatureInfo(gc.gis.geom.GCGeometry.fromGeometryList(geoms));
    }
};

/**
 * Führt eine Abfrage anhand der gewählten Geometrie für die gewählten Abfrage-Layer durch.
 *
 * @method
 * @return {gc.gis.geom.GCGeometry} geometry Abfragegeometrie
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._doFeatureInfo = function(geometry)
{
    var self = this;
    var provider = null;

    // im Popup-Modus Dialog vor der Anzeige der neuen Daten leeren
    if (!this._sidebar_mode)
    {
        this._result_list_popup.clear();
        this._info_dialog.close();
        this._info_dialog.clear();
        this._info_dialog.setContentElement(this._result_list_popup.getElement());
    }

    var layers = this._getQueryLayers();
    if(layers.length > 0)
    {
        for( var i=0; i<layers.length; i++ )
        {
        	if (layers[i] instanceof gc.gis.layer.GCGroupLayer)
        	{
                gc.gis.iface.log.warn(this, 'Objekt-Information für Gruppenlayer nicht möglich.');
			}
			else if (this._queryGeometryType !== gc.gis.module.GCFeatureInfoConstants.IDENTIFY_POINT
			    && layers[i] instanceof gc.gis.layer.GCRasterLayer
                && layers[i].isInternal()
                && layers[i].getType() === gc.gis.layer.GCLayerType.INTERNAL_WMS)
            {
                gc.gis.iface.log.info(this, 'Die Ebene "' + layers[i].getLabel() + '" unterstützt nur die Abfrage ' +
                    'nach Punkt (WMS-Ebene).');
            }
			else {
        	    // Limit bestimmen
                var result_list = this._sidebar_mode ? this._result_list_sidebar : this._result_list_popup;
                var limit = this.max_features - result_list.numFeatures(layers[i]);

                provider = gc.gis.module.GCFeatureInfoProvider.createInfoProvider(layers[i], this);
                provider.on(gc.gis.module.GCFeatureInfoProviderEvent.IDENTIFYEND, self, self._onIdentifyEnd);
                provider.identify(layers[i], geometry, Math.max(1, limit)); // Abfrage immer durchführen und erts dann Warnung ausgeben, wenn tatsächlich ein Objekt zum betroffenen Layer gefunden wurde
            }
        }
    }
    else
    {
        gc.gis.iface.log.warn(this, 'Keine abfragbare Ebene vorhanden.');
    }
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn die Abfrage der Objektliste für einen Layer (identify)
 * abgeschlossen wurde.
 *
 * @param evt
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._onIdentifyEnd = function(evt)
{
    if (evt.features.length > 0)
    {
        var layer = evt.layer;
        var result_list = this._sidebar_mode ? this._result_list_sidebar : this._result_list_popup;
        var count = this.max_features - result_list.numFeatures(layer);
        var features;

        if (count >= evt.features.length)
        {
            features = evt.features;
        }
        else if (count > 0)
        {
            features = evt.features.slice(0, count);
            gc.gis.iface.log.info(this, 'Die Ergebnisliste der Ebene "' + layer.getLabel() + '" ' +
                'hat die max. Anzahl von ' + this.max_features + ' Objekten erreicht.<br>Es konnten nicht alle ' +
                'Objekte zu Liste hinzugefügt werden.');
        }
        else
        {
            gc.gis.iface.log.info(this, 'Die Ergebnisliste der Ebene "' + layer.getLabel() + '" ' +
                'hat die max. Anzahl von ' + this.max_features + ' Objekten bereits erreicht.<br>Es wurden keine neuen ' +
                'Objekte zur Liste hinzugefügt.');
        }

        if (features)
        {
            result_list.addLayer(layer);
            result_list.addFeatures(layer, features);
            if (result_list.isLayerActive(layer))
            {
                this._trackingLayer.addFeatures(features);
            }

            if (!this._sidebar_mode)
            {
                this._info_dialog.show();
            }
        }
    }
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn ein Feature innerhalb der Liste der Sidebar angeklickt wurde.
 * Dann im Popup die Detail öffnen.
 *
 * @param evt
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._onSidebarFeatureClick = function(evt)
{
    // Dialog vorbereiten (wichtig bereits vor dem Absetzen der Anfrage => race condition bei WMS-Layern, dort ist der Content bereits vorhanden)
    this._info_dialog.clear();
    this._info_dialog.setAnimationVisibility(true);

    const provider = gc.gis.module.GCFeatureInfoProvider.createInfoProvider(evt.layer, this);
    provider.on(gc.gis.module.GCFeatureInfoProviderEvent.DETAILEND, this, this._onSidebarDetailEnd);
    provider.renderDetail(evt.layer, evt.feature);

    // Details im Dialog anzeigen
    this._info_dialog.show();
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn die Details für ein Feature innerhalb der Liste der Sidebar
 * zur Verfügung stehen (Darstellung der Details im Popup).
 *
 * @param evt
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._onSidebarDetailEnd = function(evt)
{
    this._info_dialog.setAnimationVisibility(false);
    this._info_dialog.addContent(
        evt.detailResult.content,
        evt.detailResult.contentType
    );

    // ggf. bereits hervorgehobene Features zurücksetzen
    const features = this._trackingLayerActive.getFeatures();
    for (let i = 0; i<features.length; i++)
    {
        if (features[i].getImpl() !== evt.feature.getImpl()) // Features werden durch das Hover bereits dem Tracking-Layer hinzugefügt, daher hier nicht das aktuelle entfernen
        {
            features[i].setAttribute('isShowDetail', false);
            this._trackingLayerActive.removeFeature(features[i]);
        }
    }
    evt.feature.setAttribute('isShowDetail', true);
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn ein Feature innerhalb der Liste des Popup geöffnet wird.
 *
 * @param evt
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._onPopupFeatureOpen = function(evt)
{
    var result = evt.feature.getAttribute('detailResult');
    if (result === undefined || result === null)
    {
        var provider = gc.gis.module.GCFeatureInfoProvider.createInfoProvider(evt.layer, this);
        provider.on(gc.gis.module.GCFeatureInfoProviderEvent.DETAILEND, this, this._onPopupDetailEnd);
        provider.renderDetail(evt.layer, evt.feature);
    }
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn die Details für ein Feature innerhalb der Liste des Popup
 * zur Verfügung stehen.
 *
 * @param evt
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._onPopupDetailEnd = function(evt)
{
    const feature = evt.feature;
    const container = this._result_list_popup.getFeatureContainer(evt.layer);
    if (container)
    {
        container.setFeatureDetail(
            evt.layer,
            feature,
            evt.detailResult.content,
            evt.detailResult.contentType
        );
        feature.setAttribute('detailResult', evt.detailResult);
    }
};

/**
 * Ereignisbehandlungsroutine die aufgerufen wird, wenn die Info-Dialog geschlossen wird.
 *
 * @param evt
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._onInfoDialogClosed = function(evt)
{
    if (this._sidebar_mode)
    {
        var features = this._trackingLayerActive.getFeatures();
        for (var i=0; i<features.length; i++)
        {
            features[i].setAttribute('isShowDetail', false);
            this._trackingLayerActive.removeFeature(features[i]);
        }
    }
    else
    {
        this._trackingLayer.clear();
        this._trackingLayerActive.clear();
        this._mapInteraction.clear();
    }
};

/**
 * Setzt das aktuelle Abfrageergebnis zurück.
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._resetResult = function()
{
    this._result_list_sidebar.clear();
    this._mapInteraction.clear();
    this._trackingLayer.clear();
    this._trackingLayerActive.clear();
};

/**
 * Hebt die übergebenen Features entsprechend hervor.
 *
 * @param {gc.gis.feature.GCFeature[]} features
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._highlightFeatures = function(features)
{
    if (features.length > 0)
    {
        this._trackingLayer.addFeatures(features);
    }
};

/**
 * Entfernt die einfache Hervorhebung für die übergebenen Features.
 *
 * @param {gc.gis.feature.GCFeature[]} features
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._unHighlightFeatures = function(features)
{
    this._trackingLayer.removeFeatures(features);
};

/**
 * Hebt das ein aktives Feature oder eine Liste aktiver Features entsprechend hervor.
 *
 * @param {gc.gis.feature.GCFeature|gc.gis.feature.GCFeature[]} features
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._highlightActiveFeatures = function(features)
{
    if (!Array.isArray(features)) {
        this._trackingLayerActive.addFeature(features);
    }
    else {
        this._trackingLayerActive.addFeatures(features);
    }
};

/**
 * Entfernt die Hervorhebung für ein aktiven Feature oder für eine Liste aktiver Features.
 *
 * @param {gc.gis.feature.GCFeature|gc.gis.feature.GCFeature[]} features
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._unHighlightActiveFeatures = function(features)
{
    if (!Array.isArray(features)) {
        features = [features];
    }

    if (!this._sidebar_mode)
    {
        this._trackingLayerActive.removeFeatures(features);
    }
    else
    {
        // Feature nur ausblenden, wenn es nicht gerade im Detailmodus im Dialog geöffnet ist
        for (var i=0; i<features.length; i++)
        {
            if (features[i].getAttribute('isShowDetail') !== true)
            {
                this._trackingLayerActive.removeFeature(features[i]);
            }
        }
    }
};

/**
 * Zoomt die Kartensanicht zum angegeben Features gemäß Event.
 *
 * @param {gc.gis.feature.GCFeature} feature
 * @private
 */
gc.gis.module.GCFeatureInfo.prototype._doZoomFeature = function(feature)
{
    if (feature.getGeometry())
    {
	    this._map.fitGeometry(feature.getGeometry(), {
		    constrain_resolution: true,
		    minscale: Math.min(this._map.getScale(), gc.gis.iface.ZOOM_TO_MINSCALE)
	    });
    }
    else
    {
        gc.gis.iface.log.info(this, 'Zoomen nicht möglich. Das Objekt besitzt keine Vektorgeometrie, da die Ebene keine Vektordaten bereitstellt.');
    }
};
